import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import { toast } from "react-toastify";
import { buildReferralOptions, fetchAssignableReferralPartners } from "./referralPicker";
import { useStaffMayRefer } from "hooks/useReferralPolicy";
import {
  apiErrorMessage,
  changeReferrer,
  changeRequestBody,
  getReferrerChangePreview,
  getReferrerHistory,
} from "./referrerChangeApi";

const money = (value) => Number(value || 0).toFixed(2);

const HoldingsSummary = ({ preview }) =>
  preview.rowCount > 0 ? (
    <p className="text-sm m-0">
      {preview.currentLabel} holds {preview.rowCount} earning(s) for this client: owed{" "}
      {money(preview.accrued)}, approved {money(preview.approved)}, paid {money(preview.paid)}.
    </p>
  ) : (
    <p className="text-sm m-0">
      Nothing has been earned for this client yet, so there is nothing to keep or move.
    </p>
  );

const splitWarnings = (preview, mode) =>
  mode === "MOVE"
    ? (preview?.payouts || [])
        .filter((p) => p.shared)
        .map(
          (p) =>
            `Payout #${p.payoutId} also covers other clients and will be split: ${money(
              p.movedAmount
            )} moves.`
        )
    : [];

const HistoryList = ({ clientId }) => {
  const { data = [], isLoading } = useQuery(["referrer-history", clientId], () =>
    getReferrerHistory(clientId)
  );
  if (isLoading) return <CircularProgress size={20} />;
  if (!data.length) return <p className="text-sm m-0">No referrer changes recorded.</p>;
  return (
    <ul className="text-sm pl-4 m-0 space-y-2">
      {data.map((row) => (
        <li key={row.id}>
          <strong>{row.fromLabel || "No referrer"}</strong> →{" "}
          <strong>{row.toLabel || "No referrer"}</strong> ({row.mode}
          {row.movedRowCount ? `, ${row.movedRowCount} moved, paid ${money(row.movedPaid)}` : ""}) —{" "}
          {row.reason}
          <span className="text-app-text-muted">
            {" "}
            · {row.actorName || "System"} · {new Date(row.createdAt).toLocaleString()}
          </span>
        </li>
      ))}
    </ul>
  );
};

const ReferrerChangeDialog = ({ open, client, users = [], onClose, onChanged }) => {
  const queryClient = useQueryClient();
  const clientId = client?.id;
  const [tab, setTab] = useState("change");
  const [selection, setSelection] = useState(null);
  const [clear, setClear] = useState(false);
  const [mode, setMode] = useState(null);
  const [reason, setReason] = useState("");
  const [reviewing, setReviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const previewQuery = useQuery(
    ["referrer-change-preview", clientId],
    () => getReferrerChangePreview(clientId),
    {
      enabled: open && !!clientId,
      refetchOnMount: "always",
    }
  );
  const { data: partners = [] } = useQuery(
    ["referral-partners-assignable"],
    fetchAssignableReferralPartners,
    {
      enabled: open,
    }
  );
  const staffMayRefer = useStaffMayRefer();
  const options = useMemo(
    () => buildReferralOptions({ partners, users, includeStaff: staffMayRefer }),
    [partners, users, staffMayRefer]
  );
  const preview = previewQuery.data;
  const needsMode = preview?.rowCount > 0;
  const targetIsPerson =
    !clear && selection && !selection.inputValue && typeof selection !== "string";
  const canReview =
    (clear || selection) &&
    reason.trim() &&
    (!needsMode || (mode && (mode !== "MOVE" || targetIsPerson)));

  const reset = () => {
    setSelection(null);
    setClear(false);
    setMode(null);
    setReason("");
    setReviewing(false);
    setError(null);
    setTab("change");
  };

  const close = () => {
    reset();
    onClose();
  };

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      const result = await changeReferrer(
        clientId,
        changeRequestBody({ selection, clear, mode, reason, preview })
      );
      toast.success("Referrer changed");
      queryClient.invalidateQueries(["referrer-history", clientId]);
      reset();
      onChanged(result);
    } catch (err) {
      setReviewing(false);
      setError(apiErrorMessage(err, "The referrer could not be changed"));
      if (err?.response?.status === 409) {
        previewQuery.refetch();
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog
        open={open && !reviewing}
        onClose={saving ? undefined : close}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Change referrer</DialogTitle>
        <DialogContent className="space-y-4">
          <Tabs value={tab} onChange={(_, value) => setTab(value)}>
            <Tab value="change" label="Change" />
            <Tab value="history" label="History" />
          </Tabs>
          {tab === "history" ? (
            <HistoryList clientId={clientId} />
          ) : previewQuery.isLoading || !preview ? (
            <CircularProgress size={24} />
          ) : (
            <div className="space-y-4">
              {error && <Alert severity="error">{error}</Alert>}
              <p className="text-sm m-0">
                Current referrer: <strong>{preview.currentLabel || "none"}</strong>
              </p>
              <HoldingsSummary preview={preview} />
              <Autocomplete
                freeSolo
                disabled={clear}
                options={options}
                groupBy={(o) => (o.kind === "PARTNER" ? "Referral partners" : "Staff")}
                getOptionLabel={(o) => (typeof o === "string" ? o : o.inputValue || o.label || "")}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <div className="min-w-0">
                      <div>{option.label}</div>
                      {option.sublabel ? (
                        <div className="text-xs text-app-text-muted truncate">
                          {option.sublabel}
                        </div>
                      ) : null}
                    </div>
                  </li>
                )}
                value={selection}
                onChange={(_, value) =>
                  setSelection(typeof value === "string" ? { inputValue: value } : value)
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="New referrer"
                    helperText={
                      selection?.sublabel
                        ? `Selected: ${selection.sublabel}`
                        : "Pick a person, or type a name for a custom referrer"
                    }
                  />
                )}
              />
              <FormControlLabel
                control={<Checkbox checked={clear} onChange={(e) => setClear(e.target.checked)} />}
                label="Remove the referrer"
              />
              {needsMode && (
                <RadioGroup value={mode || ""} onChange={(e) => setMode(e.target.value)}>
                  <FormControlLabel
                    value="KEEP"
                    control={<Radio />}
                    label={`Keep with ${preview.currentLabel}`}
                  />
                  <FormControlLabel
                    value="MOVE"
                    control={<Radio />}
                    label="Move to the new referrer (payments included)"
                    disabled={!targetIsPerson}
                  />
                </RadioGroup>
              )}
              <TextField
                label="Reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                multiline
                minRows={2}
                fullWidth
                inputProps={{ maxLength: 1000 }}
              />
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={saving}>
            Cancel
          </Button>
          {tab === "change" && (
            <Button variant="contained" disabled={!canReview} onClick={() => setReviewing(true)}>
              Review change
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={open && reviewing}
        onClose={saving ? undefined : () => setReviewing(false)}
        aria-labelledby="referrer-review-title"
      >
        <DialogTitle id="referrer-review-title">Confirm referrer change</DialogTitle>
        <DialogContent className="space-y-2">
          <p className="text-sm m-0">
            {preview?.currentLabel || "No referrer"} →{" "}
            <strong>{clear ? "No referrer" : selection?.label || selection?.inputValue}</strong>
          </p>
          {needsMode && (
            <p className="text-sm m-0">
              {mode === "MOVE"
                ? `All ${preview.rowCount} earning(s), including paid ${money(
                    preview.paid
                  )}, move to the new referrer.`
                : `The ${preview.rowCount} recorded earning(s) stay with ${preview.currentLabel}.`}
            </p>
          )}
          {splitWarnings(preview, mode).map((w) => (
            <Alert key={w} severity="warning">
              {w}
            </Alert>
          ))}
          <p className="text-sm m-0">Reason: {reason.trim()}</p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewing(false)} disabled={saving}>
            Back
          </Button>
          <Button variant="contained" color="warning" onClick={submit} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : "Change referrer"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ReferrerChangeDialog;
